import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  Search,
  Filter,
  RefreshCw,
  Eye,
  CheckCircle,
  Clock,
  Truck,
  MapPin,
  ChefHat,
  PackageCheck,
  XCircle,
  Phone,
  User,
  Calendar,
  CreditCard,
  Banknote,
  Building2,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  DollarSign,
  Navigation,
  Play,
  ClipboardList,
} from "lucide-react";

// Configuraciones de estados y métodos de pago
const estadoConfig = {
  recibido: { icon: Package, label: "Recibido", color: "bg-blue-500", textColor: "text-blue-600", bgLight: "bg-blue-50" },
  pago_pendiente: { icon: CreditCard, label: "Pago Pendiente", color: "bg-yellow-500", textColor: "text-yellow-600", bgLight: "bg-yellow-50" },
  pago_confirmado: { icon: CheckCircle, label: "Pago Confirmado", color: "bg-green-500", textColor: "text-green-600", bgLight: "bg-green-50" },
  ya_pagado: { icon: CheckCircle, label: "Ya Pagado", color: "bg-emerald-500", textColor: "text-emerald-600", bgLight: "bg-emerald-50" },
  confirmado: { icon: CheckCircle, label: "Confirmado", color: "bg-green-500", textColor: "text-green-600", bgLight: "bg-green-50" },
  en_produccion: { icon: ChefHat, label: "En Producción", color: "bg-orange-500", textColor: "text-orange-600", bgLight: "bg-orange-50" },
  listo: { icon: PackageCheck, label: "Listo", color: "bg-purple-500", textColor: "text-purple-600", bgLight: "bg-purple-50" },
  en_ruta: { icon: Truck, label: "En Ruta", color: "bg-blue-600", textColor: "text-blue-700", bgLight: "bg-blue-50" },
  entregado: { icon: MapPin, label: "Entregado", color: "bg-green-600", textColor: "text-green-700", bgLight: "bg-green-50" },
  cancelado: { icon: XCircle, label: "Cancelado", color: "bg-red-500", textColor: "text-red-600", bgLight: "bg-red-50" },
};

const metodoPagoConfig = {
  pago_vendedor: { icon: Banknote, label: "Pago Vendedor", color: "text-green-600" },
  efectivo_vendedor: { icon: Banknote, label: "Efectivo Vendedor", color: "text-green-600" },
  transferencia_vendedor: { icon: Phone, label: "Transfer. Vendedor", color: "text-blue-600" },
  transferencia_baruj: { icon: Building2, label: "Transfer. Baruj", color: "text-purple-600" },
  contra_entrega: { icon: Truck, label: "Contra Entrega", color: "text-orange-600" },
};

const franjaConfig = {
  manana: { label: "Mañana (8am-12m)", order: 1 },
  tarde: { label: "Tarde (12m-5pm)", order: 2 },
  noche: { label: "Noche (5pm-8pm)", order: 3 },
};

export default function AdminCatalogoUnificado() {
  // Tab activa: "pedidos" | "produccion" | "despacho"
  const [activeTab, setActiveTab] = useState("pedidos");
  
  // Datos compartidos
  const [pedidosAll, setPedidosAll] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroVendedor, setFiltroVendedor] = useState("");
  const [filtroMetodoPago, setFiltroMetodoPago] = useState("");
  const [filtroFechaInicio, setFiltroFechaInicio] = useState("");
  const [filtroFechaFin, setFiltroFechaFin] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [fechaProduccion, setFechaProduccion] = useState(() => new Date().toISOString().split("T")[0]);
  
  // Modals
  const [detalleDialog, setDetalleDialog] = useState({ open: false, pedido: null });
  const [confirmarPagoDialog, setConfirmarPagoDialog] = useState({ open: false, pedido: null, referencia: "" });
  const [cambiarEstadoDialog, setCambiarEstadoDialog] = useState({ open: false, pedido: null, nuevoEstado: "" });
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch de datos principal
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/catalogo/admin/orders?";
      if (filtroEstado) url += `estado=${filtroEstado}&`;
      if (filtroVendedor) url += `vendedor_id=${filtroVendedor}&`;
      if (filtroMetodoPago) url += `metodo_pago=${filtroMetodoPago}&`;
      if (filtroFechaInicio) url += `fecha_inicio=${filtroFechaInicio}&`;
      if (filtroFechaFin) url += `fecha_fin=${filtroFechaFin}&`;
      
      const [pedidosRes, vendedoresRes] = await Promise.all([
        api.get(url),
        api.get("/usuarios?rol=vendedor")
      ]);
      
      setPedidosAll(pedidosRes.data.pedidos || []);
      setVendedores(vendedoresRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroVendedor, filtroMetodoPago, filtroFechaInicio, filtroFechaFin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helpers
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "America/Bogota"
    });
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("es-CO", {
      weekday: "short",
      day: "numeric",
      month: "short",
      timeZone: "America/Bogota"
    });
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  // Filtrar pedidos localmente
  const pedidosFiltrados = pedidosAll.filter(p => {
    if (!busqueda) return true;
    const search = busqueda.toLowerCase();
    return (
      p.numero_pedido?.toLowerCase().includes(search) ||
      p.cliente_nombre?.toLowerCase().includes(search) ||
      p.cliente_telefono?.includes(search) ||
      p.vendedor_nombre?.toLowerCase().includes(search)
    );
  });

  // Pedidos para producción (confirmados o en_produccion, filtrados por fecha)
  const pedidosProduccion = pedidosFiltrados.filter(p => 
    ["confirmado", "en_produccion", "listo"].includes(p.estado) &&
    p.fecha_entrega === fechaProduccion
  );

  // Pedidos para despacho (listo o en_ruta, filtrados por fecha)
  const pedidosDespacho = pedidosFiltrados.filter(p => 
    ["listo", "en_ruta"].includes(p.estado) &&
    p.tipo_entrega === "domicilio" &&
    p.fecha_entrega === fechaProduccion
  );

  // Contadores para stats
  const contadores = pedidosAll.reduce((acc, p) => {
    acc[p.estado] = (acc[p.estado] || 0) + 1;
    return acc;
  }, {});

  const totalMonto = pedidosAll.reduce((sum, p) => sum + (p.total || 0), 0);

  // Resumen de productos para producción
  const resumenProductos = pedidosProduccion.reduce((acc, p) => {
    (p.items || []).forEach(item => {
      const nombre = item.producto_nombre;
      if (!acc[nombre]) acc[nombre] = { cantidad: 0, pedidos: 0 };
      acc[nombre].cantidad += item.cantidad;
      acc[nombre].pedidos += 1;
    });
    return acc;
  }, {});

  // Pedidos por zona para despacho
  const porZona = pedidosDespacho.reduce((acc, p) => {
    const zona = p.barrio_entrega || "Sin zona";
    if (!acc[zona]) acc[zona] = [];
    acc[zona].push(p);
    return acc;
  }, {});

  // Pendientes de confirmar pago bancario
  const pedidosPendientesPago = pedidosAll.filter(
    p => p.metodo_pago === "transferencia_baruj" && p.estado_pago === "pendiente"
  );

  // Acciones
  const handleConfirmarPago = async () => {
    try {
      await api.post(`/catalogo/admin/orders/${confirmarPagoDialog.pedido.id}/confirm-bank-payment`, null, {
        params: { referencia: confirmarPagoDialog.referencia }
      });
      toast.success("Pago confirmado exitosamente");
      setConfirmarPagoDialog({ open: false, pedido: null, referencia: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al confirmar pago");
    }
  };

  const handleCambiarEstado = async () => {
    try {
      await api.patch(`/catalogo/admin/orders/${cambiarEstadoDialog.pedido.id}/status`, null, {
        params: { nuevo_estado: cambiarEstadoDialog.nuevoEstado }
      });
      toast.success("Estado actualizado");
      setCambiarEstadoDialog({ open: false, pedido: null, nuevoEstado: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleStartProduction = async (pedidoId) => {
    try {
      await api.post(`/catalogo/produccion/orders/${pedidoId}/start`);
      toast.success("Pedido en producción");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleMarkReady = async (pedidoId) => {
    try {
      await api.post(`/catalogo/produccion/orders/${pedidoId}/ready`);
      toast.success("Pedido listo para despacho");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleDispatch = async (pedidoId) => {
    try {
      await api.post(`/catalogo/despacho/orders/${pedidoId}/dispatch`);
      toast.success("Pedido en ruta");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleDeliver = async (pedidoId) => {
    try {
      await api.post(`/catalogo/despacho/orders/${pedidoId}/deliver`);
      toast.success("Pedido entregado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  // Export
  const exportToExcel = () => {
    const headers = ["Número","Fecha","Vendedor","Cliente","Teléfono","Productos","Total","Método Pago","Estado","Estado Pago","Entrega","Fecha Entrega","Dirección"];
    const rows = pedidosFiltrados.map(p => [
      p.numero_pedido,
      formatDate(p.fecha_creacion),
      p.vendedor_nombre,
      p.cliente_nombre,
      p.cliente_telefono,
      p.items?.map(i => `${i.cantidad}x ${i.producto_nombre}`).join("; "),
      p.total,
      metodoPagoConfig[p.metodo_pago]?.label || p.metodo_pago,
      estadoConfig[p.estado]?.label || p.estado,
      p.estado_pago,
      p.tipo_entrega,
      p.fecha_entrega,
      p.direccion_entrega || ""
    ]);
    const csvContent = [headers.join(","), ...rows.map(row => row.map(cell => `"${cell || ""}"`).join(","))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `pedidos_catalogo_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Exportado exitosamente");
  };

  // Pedido Row Component
  const PedidoRow = ({ pedido, showProduccionActions, showDespachoActions }) => {
    const estadoCfg = estadoConfig[pedido.estado] || estadoConfig.recibido;
    const metodoCfg = metodoPagoConfig[pedido.metodo_pago] || metodoPagoConfig.efectivo_vendedor;
    const isExpanded = expandedRows.has(pedido.id);
    const needsPaymentConfirm = pedido.metodo_pago === "transferencia_baruj" && pedido.estado_pago === "pendiente";
    const unidades = (pedido.items || []).reduce((sum, i) => sum + i.cantidad, 0);
    const esContraEntrega = pedido.metodo_pago === "contra_entrega";

    return (
      <React.Fragment>
        <tr className={`hover:bg-torta-cream/30 ${needsPaymentConfirm ? "bg-purple-50/50" : ""}`}>
          <td className="p-3">
            <div className="flex items-center gap-2">
              <button onClick={() => toggleRow(pedido.id)} className="text-torta-muted hover:text-torta-brown">
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <div>
                <p className="font-display text-torta-brown font-semibold text-sm">{pedido.numero_pedido}</p>
                <p className="text-xs text-torta-muted">{formatDate(pedido.fecha_creacion)}</p>
              </div>
            </div>
          </td>
          <td className="p-3">
            <p className="font-medium text-torta-dark text-sm">{pedido.cliente_nombre}</p>
            <p className="text-xs text-torta-muted flex items-center gap-1">
              <Phone className="w-3 h-3" /> {pedido.cliente_telefono}
            </p>
          </td>
          <td className="p-3">
            <p className="text-sm text-torta-dark">{pedido.vendedor_nombre}</p>
          </td>
          <td className="p-3">
            <p className="font-display text-torta-dark">{formatCurrency(pedido.total)}</p>
            <p className="text-xs text-torta-muted">{unidades} uds</p>
          </td>
          <td className="p-3">
            <div className={`flex items-center gap-1 ${metodoCfg.color}`}>
              <metodoCfg.icon className="w-3 h-3" />
              <span className="text-xs">{metodoCfg.label}</span>
            </div>
            {needsPaymentConfirm && <span className="text-xs text-purple-600 font-medium block">⚠️ Confirmar</span>}
            {pedido.estado_pago === "confirmado_admin" && <span className="text-xs text-green-600 block">✓ Pago OK</span>}
          </td>
          <td className="p-3">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${estadoCfg.color}`}>
              <estadoCfg.icon className="w-3 h-3" />
              {estadoCfg.label}
            </span>
          </td>
          <td className="p-3">
            <p className="text-sm text-torta-dark">{formatDeliveryDate(pedido.fecha_entrega)}</p>
            <p className="text-xs text-torta-muted">
              {pedido.franja_horaria === "manana" && "Mañana"}
              {pedido.franja_horaria === "tarde" && "Tarde"}
              {pedido.franja_horaria === "noche" && "Noche"}
            </p>
          </td>
          <td className="p-3">
            <div className="flex items-center justify-end gap-1 flex-wrap">
              {/* Acciones contextuales según tab */}
              {needsPaymentConfirm && (
                <Button size="sm" onClick={() => setConfirmarPagoDialog({ open: true, pedido, referencia: "" })} className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 py-1 h-7">
                  <CheckCircle className="w-3 h-3 mr-1" />Pago
                </Button>
              )}
              
              {showProduccionActions && pedido.estado === "confirmado" && (
                <Button size="sm" onClick={() => handleStartProduction(pedido.id)} className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-2 py-1 h-7">
                  <Play className="w-3 h-3 mr-1" />Producir
                </Button>
              )}
              
              {showProduccionActions && pedido.estado === "en_produccion" && (
                <Button size="sm" onClick={() => handleMarkReady(pedido.id)} className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 h-7">
                  <PackageCheck className="w-3 h-3 mr-1" />Listo
                </Button>
              )}
              
              {showDespachoActions && pedido.estado === "listo" && (
                <Button size="sm" onClick={() => handleDispatch(pedido.id)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 h-7">
                  <Navigation className="w-3 h-3 mr-1" />Ruta
                </Button>
              )}
              
              {showDespachoActions && pedido.estado === "en_ruta" && (
                <Button size="sm" onClick={() => handleDeliver(pedido.id)} className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 h-7">
                  <CheckCircle className="w-3 h-3 mr-1" />Entregado
                </Button>
              )}
              
              <Button size="sm" variant="outline" onClick={() => setCambiarEstadoDialog({ open: true, pedido, nuevoEstado: pedido.estado })} className="text-xs px-2 py-1 h-7">
                Estado
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setDetalleDialog({ open: true, pedido })} className="text-xs px-2 py-1 h-7">
                <Eye className="w-4 h-4" />
              </Button>
            </div>
          </td>
        </tr>
        
        {/* Fila expandida */}
        {isExpanded && (
          <tr className="bg-torta-cream/20">
            <td colSpan={8} className="p-3">
              <div className="grid md:grid-cols-3 gap-3">
                {/* Productos */}
                <div className="bg-white rounded-xl p-3 border border-torta-sand/50">
                  <h4 className="font-medium text-torta-dark text-sm mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4 text-torta-brown" /> Productos
                  </h4>
                  <div className="space-y-1">
                    {pedido.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{item.cantidad}x {item.producto_nombre}</span>
                        <span className="text-torta-brown">{formatCurrency(item.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Entrega */}
                <div className="bg-white rounded-xl p-3 border border-torta-sand/50">
                  <h4 className="font-medium text-torta-dark text-sm mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-torta-brown" /> Entrega
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-torta-muted">Tipo:</span> {pedido.tipo_entrega === "domicilio" ? "Domicilio" : "Recoger"}</p>
                    {pedido.direccion_entrega && <p><span className="text-torta-muted">Dir:</span> {pedido.direccion_entrega}</p>}
                    {pedido.barrio_entrega && <p><span className="text-torta-muted">Barrio:</span> {pedido.barrio_entrega}</p>}
                    {pedido.notas_entrega && <p><span className="text-torta-muted">Ref:</span> {pedido.notas_entrega}</p>}
                  </div>
                  {pedido.direccion_entrega && (
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pedido.direccion_entrega + ", " + (pedido.barrio_entrega || ""))}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs mt-2">
                      <Navigation className="w-3 h-3" /> Ver en mapa
                    </a>
                  )}
                </div>
                
                {/* Contacto */}
                <div className="bg-white rounded-xl p-3 border border-torta-sand/50">
                  <h4 className="font-medium text-torta-dark text-sm mb-2 flex items-center gap-2">
                    <User className="w-4 h-4 text-torta-brown" /> Contacto
                  </h4>
                  <div className="space-y-2">
                    <a href={`https://wa.me/57${pedido.cliente_telefono?.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm">
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                    <a href={`tel:${pedido.cliente_telefono}`} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm">
                      <Phone className="w-4 h-4" /> Llamar
                    </a>
                  </div>
                  {pedido.notas && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded-lg">
                      <p className="text-xs text-yellow-800"><strong>Nota:</strong> {pedido.notas}</p>
                    </div>
                  )}
                  {esContraEntrega && (
                    <div className="mt-2 p-2 bg-orange-50 rounded-lg flex items-center justify-between">
                      <span className="text-xs text-orange-800 font-medium">Cobrar:</span>
                      <span className="font-display text-orange-600">{formatCurrency(pedido.total)}</span>
                    </div>
                  )}
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-4" data-testid="admin-catalogo-unificado">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl text-torta-dark">Catálogo Digital</h1>
          <p className="text-torta-muted text-sm">Gestión unificada de pedidos, producción y despacho</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} size="sm">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button variant="outline" onClick={exportToExcel} className="border-green-300 text-green-600 hover:bg-green-50" size="sm">
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-sm border border-torta-sand/50 overflow-hidden">
        <div className="flex border-b border-torta-sand/50">
          <button
            onClick={() => setActiveTab("pedidos")}
            className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === "pedidos" 
                ? "bg-torta-brown text-white" 
                : "text-torta-muted hover:bg-torta-cream/50"
            }`}
            data-testid="tab-pedidos"
          >
            <ClipboardList className="w-4 h-4" />
            <span className="hidden sm:inline">Pedidos</span>
            <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">{pedidosAll.length}</span>
          </button>
          <button
            onClick={() => setActiveTab("produccion")}
            className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === "produccion" 
                ? "bg-orange-500 text-white" 
                : "text-torta-muted hover:bg-torta-cream/50"
            }`}
            data-testid="tab-produccion"
          >
            <ChefHat className="w-4 h-4" />
            <span className="hidden sm:inline">Producción</span>
            <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">{contadores.confirmado || 0}</span>
          </button>
          <button
            onClick={() => setActiveTab("despacho")}
            className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === "despacho" 
                ? "bg-blue-600 text-white" 
                : "text-torta-muted hover:bg-torta-cream/50"
            }`}
            data-testid="tab-despacho"
          >
            <Truck className="w-4 h-4" />
            <span className="hidden sm:inline">Despacho</span>
            <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">{contadores.listo || 0}</span>
          </button>
        </div>

        {/* Stats Cards compactos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-torta-cream/30">
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-torta-muted text-xs">Total</p>
                <p className="font-display text-lg text-torta-dark">{pedidosAll.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-torta-muted text-xs">Monto</p>
                <p className="font-display text-lg text-torta-dark">{formatCurrency(totalMonto)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-torta-muted text-xs">Pendientes</p>
                <p className="font-display text-lg text-torta-dark">{contadores.recibido || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-torta-muted text-xs">En Producción</p>
                <p className="font-display text-lg text-torta-dark">{contadores.en_produccion || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Alerta pagos pendientes */}
        {pedidosPendientesPago.length > 0 && (
          <div className="mx-4 mb-4 bg-purple-50 border border-purple-200 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Building2 className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-purple-800 text-sm">
                  {pedidosPendientesPago.length} transferencia(s) pendiente(s) de confirmar
                </p>
                <p className="text-purple-600 text-xs">Verifica en la cuenta bancaria y confirma.</p>
              </div>
            </div>
          </div>
        )}

        {/* CONTENIDO DE TABS */}
        <div className="p-4">
          {/* Tab: Pedidos */}
          {activeTab === "pedidos" && (
            <div className="space-y-4">
              {/* Filtros */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-torta-muted" />
                  <Input placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="pl-10 h-9" />
                </div>
                <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className={showFilters ? "border-torta-brown text-torta-brown" : ""} size="sm">
                  <Filter className="w-4 h-4 mr-1" />
                  Filtros
                </Button>
              </div>

              {showFilters && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3 bg-torta-cream/30 rounded-xl">
                  <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      {Object.entries(estadoConfig).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filtroVendedor} onValueChange={setFiltroVendedor}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Vendedor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      {vendedores.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filtroMetodoPago} onValueChange={setFiltroMetodoPago}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Método Pago" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Todos</SelectItem>
                      {Object.entries(metodoPagoConfig).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={filtroFechaInicio} onChange={(e) => setFiltroFechaInicio(e.target.value)} className="h-8 text-xs" placeholder="Desde" />
                  <Input type="date" value={filtroFechaFin} onChange={(e) => setFiltroFechaFin(e.target.value)} className="h-8 text-xs" placeholder="Hasta" />
                </div>
              )}

              {/* Quick filters */}
              <div className="flex flex-wrap gap-1">
                <button onClick={() => setFiltroEstado("")} className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${!filtroEstado ? "bg-torta-brown text-white" : "bg-torta-cream text-torta-brown hover:bg-torta-sand"}`}>
                  Todos ({pedidosAll.length})
                </button>
                {Object.entries(estadoConfig).slice(0, 5).map(([key, val]) => (
                  <button key={key} onClick={() => setFiltroEstado(key)} className={`px-2 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${filtroEstado === key ? `${val.color} text-white` : `${val.bgLight} ${val.textColor} hover:opacity-80`}`}>
                    <val.icon className="w-3 h-3" />
                    {contadores[key] || 0}
                  </button>
                ))}
              </div>

              {/* Tabla */}
              <div className="overflow-x-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-8 h-8 text-torta-brown animate-spin mx-auto mb-2" />
                    <p className="text-torta-muted">Cargando...</p>
                  </div>
                ) : pedidosFiltrados.length === 0 ? (
                  <div className="p-8 text-center">
                    <Package className="w-12 h-12 text-torta-sand mx-auto mb-3" />
                    <p className="text-torta-muted">No hay pedidos</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-torta-cream/50">
                      <tr>
                        <th className="text-left p-3 font-medium text-torta-dark">Pedido</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Cliente</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Vendedor</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Total</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Pago</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Estado</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Entrega</th>
                        <th className="text-right p-3 font-medium text-torta-dark">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-torta-sand/30">
                      {pedidosFiltrados.map((pedido) => (
                        <PedidoRow key={pedido.id} pedido={pedido} showProduccionActions={false} showDespachoActions={false} />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab: Producción */}
          {activeTab === "produccion" && (
            <div className="space-y-4">
              {/* Selector de fecha */}
              <div className="flex items-center gap-3">
                <Label className="text-sm text-torta-muted">Fecha de entrega:</Label>
                <Input type="date" value={fechaProduccion} onChange={(e) => setFechaProduccion(e.target.value)} className="w-40 h-9" />
              </div>

              {/* Resumen de productos */}
              {Object.keys(resumenProductos).length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h3 className="font-display text-orange-800 mb-3 flex items-center gap-2">
                    <ChefHat className="w-5 h-5" />
                    Resumen para {new Date(fechaProduccion + "T12:00:00").toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long" })}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {Object.entries(resumenProductos).map(([producto, data]) => (
                      <div key={producto} className="bg-white rounded-lg p-2 text-center">
                        <p className="font-display text-xl text-orange-600">{data.cantidad}</p>
                        <p className="text-xs text-torta-dark font-medium truncate">{producto}</p>
                        <p className="text-xs text-torta-muted">{data.pedidos} pedido(s)</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista de pedidos */}
              <div className="overflow-x-auto">
                {pedidosProduccion.length === 0 ? (
                  <div className="p-8 text-center">
                    <ChefHat className="w-12 h-12 text-torta-sand mx-auto mb-3" />
                    <p className="text-torta-muted">No hay pedidos para producir en esta fecha</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-orange-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-torta-dark">Pedido</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Cliente</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Vendedor</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Total</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Pago</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Estado</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Entrega</th>
                        <th className="text-right p-3 font-medium text-torta-dark">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-torta-sand/30">
                      {pedidosProduccion.map((pedido) => (
                        <PedidoRow key={pedido.id} pedido={pedido} showProduccionActions={true} showDespachoActions={false} />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Tab: Despacho */}
          {activeTab === "despacho" && (
            <div className="space-y-4">
              {/* Selector de fecha */}
              <div className="flex items-center gap-3">
                <Label className="text-sm text-torta-muted">Fecha de entrega:</Label>
                <Input type="date" value={fechaProduccion} onChange={(e) => setFechaProduccion(e.target.value)} className="w-40 h-9" />
              </div>

              {/* Por zona */}
              {Object.keys(porZona).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-display text-blue-800 mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Pedidos por Zona
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(porZona).map(([zona, pedidosZona]) => (
                      <div key={zona} className="bg-white rounded-lg px-3 py-1 flex items-center gap-2">
                        <span className="font-medium text-blue-800 text-sm">{zona}</span>
                        <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">{pedidosZona.length}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista de pedidos */}
              <div className="overflow-x-auto">
                {pedidosDespacho.length === 0 ? (
                  <div className="p-8 text-center">
                    <Truck className="w-12 h-12 text-torta-sand mx-auto mb-3" />
                    <p className="text-torta-muted">No hay pedidos para despachar en esta fecha</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-blue-50">
                      <tr>
                        <th className="text-left p-3 font-medium text-torta-dark">Pedido</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Cliente</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Vendedor</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Total</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Pago</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Estado</th>
                        <th className="text-left p-3 font-medium text-torta-dark">Entrega</th>
                        <th className="text-right p-3 font-medium text-torta-dark">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-torta-sand/30">
                      {pedidosDespacho.map((pedido) => (
                        <PedidoRow key={pedido.id} pedido={pedido} showProduccionActions={false} showDespachoActions={true} />
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dialog: Detalle del pedido */}
      <Dialog open={detalleDialog.open} onOpenChange={(open) => setDetalleDialog({ open, pedido: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-torta-brown" />
              Pedido {detalleDialog.pedido?.numero_pedido}
            </DialogTitle>
          </DialogHeader>
          {detalleDialog.pedido && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-torta-muted">Cliente</Label>
                  <p className="font-medium">{detalleDialog.pedido.cliente_nombre}</p>
                  <p className="text-sm text-torta-muted">{detalleDialog.pedido.cliente_telefono}</p>
                </div>
                <div>
                  <Label className="text-torta-muted">Vendedor</Label>
                  <p className="font-medium">{detalleDialog.pedido.vendedor_nombre}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-torta-muted">Productos</Label>
                <div className="mt-2 space-y-2">
                  {detalleDialog.pedido.items?.map((item, i) => (
                    <div key={i} className="flex justify-between bg-torta-cream/50 p-2 rounded">
                      <span>{item.cantidad}x {item.producto_nombre}</span>
                      <span className="font-medium">{formatCurrency(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t font-display text-lg">
                  <span>Total</span>
                  <span className="text-torta-brown">{formatCurrency(detalleDialog.pedido.total)}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-torta-muted">Entrega</Label>
                  <p>{formatDeliveryDate(detalleDialog.pedido.fecha_entrega)}</p>
                  <p className="text-sm">{detalleDialog.pedido.direccion_entrega || "Recoger en punto"}</p>
                </div>
                <div>
                  <Label className="text-torta-muted">Método de Pago</Label>
                  <p>{metodoPagoConfig[detalleDialog.pedido.metodo_pago]?.label}</p>
                  <p className="text-sm">Estado: {detalleDialog.pedido.estado_pago}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmar pago bancario */}
      <Dialog open={confirmarPagoDialog.open} onOpenChange={(open) => setConfirmarPagoDialog({ open, pedido: null, referencia: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Confirmar Transferencia Bancaria
            </DialogTitle>
          </DialogHeader>
          {confirmarPagoDialog.pedido && (
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-xl">
                <p className="font-medium text-purple-800">Pedido: {confirmarPagoDialog.pedido.numero_pedido}</p>
                <p className="text-purple-600">Monto: {formatCurrency(confirmarPagoDialog.pedido.total)}</p>
                <p className="text-purple-600 text-sm">Cliente: {confirmarPagoDialog.pedido.cliente_nombre}</p>
              </div>
              
              <div>
                <Label>Número de referencia (opcional)</Label>
                <Input
                  placeholder="Ej: 123456789"
                  value={confirmarPagoDialog.referencia}
                  onChange={(e) => setConfirmarPagoDialog(prev => ({ ...prev, referencia: e.target.value }))}
                />
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setConfirmarPagoDialog({ open: false, pedido: null, referencia: "" })}>
                  Cancelar
                </Button>
                <Button onClick={handleConfirmarPago} className="bg-purple-600 hover:bg-purple-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Pago
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Cambiar estado */}
      <Dialog open={cambiarEstadoDialog.open} onOpenChange={(open) => setCambiarEstadoDialog({ open, pedido: null, nuevoEstado: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Estado del Pedido</DialogTitle>
          </DialogHeader>
          {cambiarEstadoDialog.pedido && (
            <div className="space-y-4">
              <p className="text-torta-muted">
                Pedido <strong>{cambiarEstadoDialog.pedido.numero_pedido}</strong>
              </p>
              
              <div>
                <Label>Nuevo Estado</Label>
                <Select 
                  value={cambiarEstadoDialog.nuevoEstado} 
                  onValueChange={(val) => setCambiarEstadoDialog(prev => ({ ...prev, nuevoEstado: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(estadoConfig).map(([key, val]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <val.icon className="w-4 h-4" />
                          {val.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCambiarEstadoDialog({ open: false, pedido: null, nuevoEstado: "" })}>
                  Cancelar
                </Button>
                <Button onClick={handleCambiarEstado}>
                  Guardar Cambio
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
